import { useEffect } from 'react';

import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import exerciseQueuesApi, {
  exerciseQueuesApiKeys,
  exerciseQueuesAvailabilityQueryOptions,
} from 'api/rest/exerciseQueues/exerciseQueues';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import { ON_GO_ACCESS } from 'constants/onGoAccess.config';
import PATHS from 'constants/paths';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';
import useOnGoAccess from 'hooks/useOnGoAccess/useOnGoAccess';
import useSubjectStore from 'storages/subject';

export const getOnGoAdditionalText = (
  hasOnGoAccess: boolean,
  onGoAvailability?: { available: boolean } | null,
  isDailyLimitReached = false,
): string | null => {
  if (onGoAvailability && !onGoAvailability.available) {
    return 'Niedostepne dla tego przedmiotu';
  }

  if (!hasOnGoAccess) {
    return 'Pracujemy nad tym';
  }

  if (isDailyLimitReached) {
    return ON_GO_ACCESS.copy.dashboard.premiumBadge;
  }

  return null;
};

const useOnGoAvailability = () => {
  const location = useLocation();
  const hasOnGoAccess = useFeatureFlag(FEATURE_FLAGS.ON_GO);
  const { subject } = useSubjectStore();
  const subjectPk = subject?.pk;
  const { canUseOnGo, refetchFeatures } = useOnGoAccess({ refetchOnWindowFocus: true });
  const isAvailabilityQueryEnabled = subjectPk != null;

  useEffect(() => {
    if (location.pathname === PATHS.ROOT) {
      refetchFeatures();
    }
  }, [location.pathname, refetchFeatures]);

  const { data: onGoAvailability } = useQuery(
    isAvailabilityQueryEnabled ? exerciseQueuesApiKeys.availability(subjectPk) : ['exerciseQueues', 'availability', 'disabled'],
    () => {
      if (subjectPk == null) {
        throw new Error('subjectPk is required');
      }

      return exerciseQueuesApi.getAvailability(subjectPk)();
    },
    {
      enabled: isAvailabilityQueryEnabled,
      ...exerciseQueuesAvailabilityQueryOptions,
    },
  );

  const isOnGoUnavailableForSubject = Boolean(onGoAvailability && !onGoAvailability.available);
  const isOnGoDailyLimitReached = hasOnGoAccess && !canUseOnGo;
  const onGoAdditionalText = getOnGoAdditionalText(hasOnGoAccess, onGoAvailability, isOnGoDailyLimitReached);
  const isOnGoDisabled = !hasOnGoAccess || isOnGoUnavailableForSubject || isOnGoDailyLimitReached;

  return {
    hasOnGoAccess,
    onGoAvailability,
    isOnGoUnavailableForSubject,
    isOnGoDailyLimitReached,
    onGoAdditionalText,
    isOnGoDisabled,
  };
};

export default useOnGoAvailability;
